import { motion } from "framer-motion";
import { AppShell } from "@/components/AppShell";
import { BentoCard } from "@/components/BentoCard";
import { ComplianceStatusDot } from "@/components/compliance/ComplianceStatusDot";
import { ProgressRing } from "@/components/ProgressRing";
import { COMPLIANCE_CATEGORIES, selectCompletion, useComplianceStore } from "@/store/complianceStore";

const EASE = [0.22, 1, 0.36, 1] as const;

const Eyebrow = ({ children }: { children: React.ReactNode }) => (
  <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-cad">{children}</span>
);

export default function ComplianceChecklistScreen() {
  const items = useComplianceStore((s) => s.items);
  const { done, total, pct } = selectCompletion(items);
  const flagged = items.filter((i) => i.status === "flagged").length;

  return (
    <AppShell>
      <section data-testid="compliance-screen" className="mx-auto flex min-h-screen w-full max-w-4xl flex-col gap-6 px-5 py-6 md:px-10 md:py-10">
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE }} className="flex items-center justify-between gap-6">
          <div className="flex flex-col gap-2">
            <Eyebrow>Reports · Approvals</Eyebrow>
            <h1 data-testid="compliance-headline" className="font-display text-4xl font-bold tracking-tight text-white sm:text-5xl">Compliance Checklist</h1>
            <p data-testid="compliance-summary" className="font-sans text-sm text-white/55">
              {done} of {total} checks are all good{flagged > 0 && <>, <span className="text-amber">{flagged} need{flagged === 1 ? "s" : ""} a look</span></>}.
            </p>
          </div>
          <ProgressRing pct={pct} label="Complete" />
        </motion.div>

        <div className="flex flex-col gap-4">
          {COMPLIANCE_CATEGORIES.map((cat, ci) => {
            const rows = items.filter((i) => i.category === cat);
            const catDone = rows.filter((r) => r.status === "compliant").length;
            return (
              <motion.div key={cat} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 + ci * 0.09, duration: 0.6, ease: EASE }}>
                <BentoCard data-testid={`compliance-group-${cat.toLowerCase()}`} className="flex flex-col">
                  <div className="flex items-center justify-between border-b border-cad/20 px-5 py-4 sm:px-6">
                    <h2 className="font-display text-base font-semibold tracking-tight text-white md:text-lg">{cat}</h2>
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-cad">{catDone} / {rows.length}</span>
                  </div>
                  <ul className="flex flex-col divide-y divide-cad/15">
                    {rows.map((row) => (
                      <li key={row.id} data-testid={`compliance-item-${row.id}`} className="flex items-start justify-between gap-4 px-5 py-4 transition-colors duration-200 hover:bg-white/[0.02] sm:px-6">
                        <div className="flex flex-col gap-1">
                          <span className="font-sans text-sm text-white">{row.text}</span>
                          {row.status === "flagged" && row.hint && (
                            <span data-testid={`compliance-hint-${row.id}`} className="font-sans text-xs leading-relaxed text-amber/80">{row.hint}</span>
                          )}
                        </div>
                        <ComplianceStatusDot status={row.status} />
                      </li>
                    ))}
                  </ul>
                </BentoCard>
              </motion.div>
            );
          })}
        </div>

        <div className="flex flex-wrap items-center gap-5 px-1 font-mono text-[10px] uppercase tracking-[0.18em] text-cad">
          <span className="flex items-center gap-2"><span className="h-3 w-3 rounded-full border-[1.5px] border-safety/80" />Still to check</span>
          <span className="flex items-center gap-2"><span className="h-3 w-3 rounded-full bg-safety" />All good</span>
          <span className="flex items-center gap-2"><span className="h-3 w-3 rounded-full border-[1.5px] border-amber" />Needs a look</span>
        </div>
      </section>
    </AppShell>
  );
}
